// Hardcoded demo data for the Education section on the Coach analytics page.
// Used only when `portalHardcodedData2026Enabled` is on — never hits the API.
// Shapes intentionally match what the backend endpoints would return so the
// component can be wired up the same way as real responses:
//
//   getPortalCoachCoursesStarted -> { results: [{id:'time',...},{id:'courses_started',...}] }
//   getPortalCoachTopCourses     -> { results: [{course_id,title,learners}], total_started }
//   getPortalCoachFhsDistribution-> { bands: [{key,label,count}], total, average }

import { getUnixTime, startOfISOWeek, startOfMonth, sub } from 'date-fns';

// Build N consecutive unix timestamps ending on the start of the current
// week/month so the chart's x-axis stays in sync with "today". The values
// returned by the real API are unix seconds at period boundaries.
// `unit` is the same string passed by callers — currently 'isoWeek' or
// 'month'. date-fns equivalents differ in spelling, hence the mapping.
const buildUnixTimeline = (count, unit) => {
  const start = unit === 'isoWeek' ? startOfISOWeek(new Date()) : startOfMonth(new Date());
  const subKey = unit === 'isoWeek' ? 'weeks' : 'months';
  return Array.from({ length: count }, (_, i) => getUnixTime(sub(start, { [subKey]: count - 1 - i })));
};

// Stable demo numbers. Mildly upward trend so the "more than the previous
// X" line in the companion KPI reads positively in the demo.
const WEEKLY_COURSES = [180, 230, 220, 290, 330, 300, 410, 470, 430, 520, 580, 620];
const MONTHLY_COURSES = [780, 1020, 1160, 1080, 1340, 1450, 1410, 1690, 1880, 2010, 2210, 2480];

// `timeSeries` is the value of TIME_SERIES.WEEK_TO_WEEK / MONTH_TO_MONTH
// from `@/utils/chart` — currently the strings 'w2w' / 'm2m'.
export const getHardcodedCoursesStarted = timeSeries => {
  const unit = timeSeries === 'w2w' ? 'isoWeek' : 'month';
  const values = timeSeries === 'w2w' ? WEEKLY_COURSES : MONTHLY_COURSES;
  const timeValues = buildUnixTimeline(values.length, unit);

  return {
    results: [
      {
        id: 'time',
        values: timeValues,
        // Real API marks the most recently *completed* period; our last
        // timestamp is the start of the current (in-progress) period, so
        // step back one to mirror that semantic.
        calculated: { last_complete: timeValues[timeValues.length - 2] },
      },
      {
        id: 'courses_completed',
        name: 'Courses completed',
        values,
        calculated: {
          last: values[values.length - 1],
          prev: values[values.length - 2],
        },
      },
    ],
  };
};

export const HARDCODED_TOP_COURSES = {
  results: [
    { course_id: 'budgeting-basics', title: 'Budgeting Basics', learners: 4600 },
    { course_id: 'understanding-credit', title: 'Understanding Credit Scores', learners: 3550 },
    { course_id: 'saving-emergencies', title: 'Saving for Emergencies', learners: 2950 },
    { course_id: 'pension-basics', title: 'Pension Basics', learners: 2400 },
    { course_id: 'first-time-buyer', title: 'First-time Buyer Essentials', learners: 1820 },
  ],
  total_started: 22500,
};

export const HARDCODED_FHS_DISTRIBUTION = {
  bands: [
    { key: 'room_for_growth', label: 'Room for growth', count: 540 },
    { key: 'fair', label: 'Fair', count: 1140 },
    { key: 'good', label: 'Good', count: 2100 },
    { key: 'very_good', label: 'Very good', count: 1440 },
    { key: 'excellent', label: 'Excellent', count: 780 },
  ],
  total: 6000,
  average: 28,
  // 6-month trend: average improved from 25 → 28. Drives the "↑ 3 pts in the
  // last 6 months" status note on the FHS tile.
  average_delta_6m: 3,
};
